'use client';

import React, { MouseEventHandler } from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import SvgTag from '@/assets/svg/icon-tag.svg';
import SvgArrow from '@/assets/svg/icon-arrow.svg';
import { paddingTopSizeMap, paddingBotSizeMap } from '@/utils/styleHelpers';

interface Props {
  buttonOpenNewWindow: boolean;
  buttonUrl: string;
  className?: string;
  iconFlip?: boolean;
  iconPosition?: string;
  iconStyle?: 'tag' | 'arrow';
  linkWrapper?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  paddingBottom?: string;
  paddingTop?: string;
  title: string;
}

const AnimatedLink = ({
  title = 'Read More',
  buttonUrl = '#',
  buttonOpenNewWindow,
  linkWrapper = 'div',
  iconStyle = 'arrow',
  iconPosition = 'right',
  iconFlip,
  onClick,
  paddingTop = '',
  paddingBottom = '',
  className,
}: Props) => {
  const getImage = () => {
    const icons = {
      tag: (
        <SvgTag
          className={classNames(
            'pointer-events-none mx-[5px] mt-[3px] *:fill-clayton-blue',
            {
              ['rotate-180']: iconFlip,
            }
          )}
        />
      ),
      arrow: (
        <SvgArrow
          className={classNames(
            `pointer-events-none mx-[5px] mt-[3px] *:fill-clayton-blue`,
            {
              ['rotate-180']: iconFlip,
            }
          )}
        />
      ),
    };

    return (
      <span
        className={classNames(
          'flex items-center transition duration-300 ease-in',
          {
            ['group-hover:translate-x-[30%]']: icons['arrow'] && !iconFlip,
            ['group-hover:translate-x-[-30%]']: icons['arrow'] && iconFlip,
            ['group-hover:translate-x-[-10%]']: icons['tag'],
          }
        )}
      >
        {icons[iconStyle] || icons.arrow}
      </span>
    );
  };

  return linkWrapper === 'div' ? (
    <div
      className={classNames('mx-auto my-0 max-w-standard px-0', {
        [paddingTopSizeMap(paddingTop)]: paddingTop,
        [paddingBotSizeMap(paddingBottom)]: paddingBottom,
      })}
    >
      <Link
        href={buttonUrl}
        className="group flex flex-row flex-nowrap justify-start text-[16px] font-bold leading-5 no-underline"
        onClick={onClick}
        target={buttonOpenNewWindow ? '_blank' : '_self'}
        rel={buttonOpenNewWindow ? 'noopener noreferrer' : ''}
      >
        {iconPosition === 'left' && getImage()}
        <p
          className={classNames(
            `font-source-sans text-[1.125rem] font-bold leading-tight text-clayton-blue`,
            [{ [`${className}`]: className }]
          )}
        >
          {title}
        </p>
        {iconPosition === 'right' && getImage()}
      </Link>
    </div>
  ) : (
    <span
      className={`mx-auto my-0 inline-block max-w-standard pt-[${paddingTop}] pb-[${paddingBottom}]`}
    >
      <Link
        href={buttonUrl}
        className="group flex flex-row flex-nowrap justify-start text-[16px] font-bold leading-5 no-underline"
        onClick={onClick}
        target={buttonOpenNewWindow ? '_blank' : '_self'}
        rel={buttonOpenNewWindow ? 'noopener noreferrer' : ''}
      >
        {iconPosition === 'left' && getImage()}
        <span
          className={classNames(
            'animated-link-txt',
            `font-source-sans text-[1.125rem] font-bold leading-tight text-clayton-blue`,
            [{ [`${className}`]: className }]
          )}
        >
          {title}
        </span>
        {iconPosition === 'right' && getImage()}
      </Link>
    </span>
  );
};

export default AnimatedLink;
